import i18next, { t } from 'i18next'
import intlTelInput from 'intl-tel-input'
// eslint-disable-next-line no-unused-vars
import { phoneErrorTypes, emailCheckRegExp } from '../constants'

function initForm(config = {}) {
  const defaultConfig = {
    formSelector: '#form',
    submitBtnSelector: '[data-submit]',
    apiEndpoint: null,
    preferredCountries: ['us', 'ua', 'pl', 'lt', 'lv'],
    utilsScript: 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.3/js/utils.js',

    selectors: {
      name: '#name',
      email: '#email',
      phone: '#phone',
      nameError: '#error-msg-name',
      emailError: '[data-email-error]',
      phoneError: '#error-msg-phone',
      captchaError: '[data-captcha-error]',
      recaptcha: '#g-recaptcha-response',

      successModal: '[data-success-modal]',
      modalOverlay: '[data-success-modal-overlay]',
      closeModalBtns: '[data-close-success-modal]',
    },

    fallbackSelectors: {
      name: '#b2b-name',
      email: '#b2b-email',
      phone: '#b2b-phone',
    },

    fields: [],
  }

  const settings = { ...defaultConfig, ...config }

  settings.selectors = { ...defaultConfig.selectors, ...config.selectors }
  settings.fallbackSelectors = {
    ...defaultConfig.fallbackSelectors,
    ...config.fallbackSelectors,
  }

  const form = document.querySelector(settings.formSelector)
  const submitBtn = document.querySelector(settings.submitBtnSelector)

  if (!form || !submitBtn) {
    console.warn('Form or submit button not found')
    return null
  }

  if (!settings.apiEndpoint) {
    console.error('apiEndpoint is required')
    return null
  }

  const formElements = initFormElements(form, settings)

  const iti = initPhoneInput(formElements.phoneInput, settings)

  attachEventListeners(form, formElements, iti, settings)

  initI18nListeners(formElements, iti)

  return { form, formElements, iti, settings }
}

function initFormElements(form, settings) {
  const { selectors, fallbackSelectors, fields } = settings

  const findElement = (primary, fallback) => {
    return form.querySelector(primary) || (fallback ? form.querySelector(fallback) : null)
  }

  const nameInput = findElement(selectors.name, fallbackSelectors.name)
  const emailInput = findElement(selectors.email, fallbackSelectors.email)
  const phoneInput = findElement(selectors.phone, fallbackSelectors.phone)

  const errorElements = {
    name: form.querySelector(selectors.nameError),
    email: form.querySelector(selectors.emailError),
    phone: form.querySelector(selectors.phoneError),
    captcha: form.querySelector(selectors.captchaError),
  }

  const additionalFields = {}
  fields.forEach(field => {
    additionalFields[field.name] = {
      input: form.querySelector(field.selector),
      error: form.querySelector(field.errorSelector),
      ...field,
    }
  })

  const overlay =
    form.querySelector(selectors.modalOverlay) ||
    document.querySelector(selectors.modalOverlay)
  const successMessage =
    form.querySelector(selectors.successModal) ||
    document.querySelector(selectors.successModal)
  const closeBtns = document.querySelectorAll(selectors.closeModalBtns)

  return {
    nameInput,
    emailInput,
    phoneInput,
    errorElements,
    additionalFields,
    overlay,
    successMessage,
    closeBtns,
  }
}

function initPhoneInput(phoneInput, settings) {
  if (!phoneInput) return null

  return intlTelInput(phoneInput, {
    utilsScript: settings.utilsScript,
    separateDialCode: true,
    nationalMode: false,
    formatOnDisplay: true,
    autoFormat: true,
    preferredCountries: settings.preferredCountries,
  })
}

function attachEventListeners(form, elements, iti, settings) {
  const { nameInput, phoneInput, additionalFields, closeBtns } = elements

  if (nameInput) {
    nameInput.addEventListener('input', handleNameInput)
  }

  if (phoneInput && iti) {
    phoneInput.addEventListener('countrychange', () =>
      handleCountryChange(iti, elements.errorElements.phone),
    )
    phoneInput.addEventListener('input', e =>
      handlePhoneInput(e, iti, elements.errorElements.phone),
    )
  }

  Object.values(additionalFields).forEach(field => {
    if (field.input && field.validation) {
      field.input.addEventListener('input', e => handleAdditionalFieldInput(e, field))
    }
  })

  closeBtns?.forEach(btn => {
    btn?.addEventListener('click', () => closeSuccessModal(elements))
  })

  form.addEventListener('submit', e => handleFormSubmit(e, form, elements, iti, settings))
}

function initI18nListeners(elements, iti) {
  i18next.on('loaded', () => {
    if (elements.nameInput) {
      elements.nameInput.addEventListener('blur', e => {
        e.preventDefault()
        const nameValue = elements.nameInput.value.trim()

        if (nameValue !== '') {
          hideErrorMsg(elements.errorElements.name)
        } else {
          showErrorMsg(elements.errorElements.name, t('custom_dedic_name_error'))
        }
      })
    }

    if (elements.emailInput) {
      elements.emailInput.addEventListener('blur', e => {
        e.preventDefault()
        const emailValue = elements.emailInput.value.trim()
        const isValidEmail = emailCheckRegExp.test(emailValue)

        if (isValidEmail) {
          hideErrorMsg(elements.errorElements.email)
        } else {
          showErrorMsg(elements.errorElements.email, t('emailError'))
        }
      })
    }

    if (elements.phoneInput && iti) {
      elements.phoneInput.addEventListener('blur', () => {
        const phoneValue = elements.phoneInput.value.trim()

        if (phoneValue !== '') {
          if (iti.isValidNumber()) {
            hideErrorMsg(elements.errorElements.phone)
          } else {
            const errorCode = iti.getValidationError()
            showErrorMsg(
              elements.errorElements.phone,
              t(phoneErrorTypes[errorCode >= 0 ? errorCode : 0]),
            )
          }
        } else {
          hideErrorMsg(elements.errorElements.phone)
        }
      })
    }

    Object.values(elements.additionalFields).forEach(field => {
      if (field.input && field.error) {
        field.input.addEventListener('blur', e => {
          e.preventDefault()
          const value = e.target.value.trim()
          const isRequired = field.required !== false

          if (isRequired && !value) {
            showErrorMsg(field.error, t(field.errorKey || 'fieldRequired'))
          } else if (value || !isRequired) {
            hideErrorMsg(field.error)
          }
        })
      }
    })
  })
}

function handleNameInput(e) {
  e.preventDefault()
  const value = e.target.value.replace(/[^\p{L}\s]+/gu, '').substring(0, 32)
  e.target.value = value
}

function handlePhoneInput(e, iti, errorElement) {
  e.preventDefault()
  const value = e.target.value.replace(/\D/g, '')

  if (iti.isValidNumber()) {
    iti.setNumber(value)
    errorElement.style.display = 'none'
  } else {
    if (iti.getValidationError() === 3) {
      e.target.value = value.slice(0, -1)
      return
    }
    e.target.value = value
  }
}

function handleCountryChange(iti, errorElement) {
  iti.setNumber('')
  hideErrorMsg(errorElement)
}

function handleAdditionalFieldInput(e, field) {
  e.preventDefault()
  let value = e.target.value

  if (field.validation) {
    if (field.validation.regex) {
      value = value.replace(field.validation.regex, '')
    }
    if (field.validation.maxLength) {
      value = value.substring(0, field.validation.maxLength)
    }
  }

  e.target.value = value
}

function validateForm(elements, iti, settings) {
  const formData = {}
  let isValid = true

  if (elements.nameInput) {
    const nameValue = elements.nameInput.value.trim()
    if (nameValue !== '') {
      hideErrorMsg(elements.errorElements.name)
      formData.name = nameValue
    } else {
      showErrorMsg(elements.errorElements.name, t('dedic_custom_name_error'))
      isValid = false
    }
  }

  if (elements.emailInput) {
    const emailValue = elements.emailInput.value.trim()
    const isValidEmail = emailCheckRegExp.test(emailValue)

    if (isValidEmail) {
      hideErrorMsg(elements.errorElements.email)
      formData.email = emailValue
    } else {
      showErrorMsg(elements.errorElements.email, t('emailError'))
      isValid = false
    }
  }

  if (elements.phoneInput && iti) {
    const phoneValue = elements.phoneInput.value.trim()

    if (phoneValue !== '') {
      if (iti.isValidNumber()) {
        hideErrorMsg(elements.errorElements.phone)
        formData.phone = iti.getNumber()
      } else {
        const errorCode = iti.getValidationError()
        showErrorMsg(
          elements.errorElements.phone,
          t(phoneErrorTypes[errorCode >= 0 ? errorCode : 0]),
        )
        isValid = false
      }
    } else {
      hideErrorMsg(elements.errorElements.phone)
      formData.phone = ''
    }
  }

  const recaptchaCheck = document.querySelector(settings.selectors.recaptcha)
  const captchaValid = recaptchaCheck?.value?.trim() !== ''

  if (!captchaValid) {
    showErrorMsg(elements.errorElements.captcha, t('captchaError'))
    isValid = false
  } else {
    hideErrorMsg(elements.errorElements.captcha)
    formData.g_recaptcha_response = window.grecaptcha.getResponse()
  }

  Object.entries(elements.additionalFields).forEach(([fieldName, field]) => {
    if (field.input && field.error) {
      const value = field.input.value.trim()
      const isRequired = field.required !== false

      if (isRequired && !value) {
        showErrorMsg(field.error, t(field.errorKey || 'fieldRequired'))
        isValid = false
      } else if (value) {
        hideErrorMsg(field.error)
        formData[fieldName] = value
      }
    }
  })

  return { isValid, formData }
}

async function handleFormSubmit(e, form, elements, iti, settings) {
  e.preventDefault()

  const submitBtn = form.querySelector(settings.submitBtnSelector)
  const originalBtnText = submitBtn.innerText

  showLoader(submitBtn)
  disableForm(form)

  const { isValid, formData } = validateForm(elements, iti, settings)

  if (!isValid) {
    hideLoader(submitBtn, originalBtnText)
    enableForm(form)
    return
  }

  try {
    const response = await fetch(`${process.env.BASE_URL}${settings.apiEndpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    })

    const result = await response.json()

    if (result?.msg?.includes('message has sent')) {
      resetForm(form)
      showSuccessModal(elements)
    }
  } catch (err) {
    console.error('Form submission error:', err)
  } finally {
    hideLoader(submitBtn, originalBtnText)
    enableForm(form)
  }
}

function showErrorMsg(elem, msg) {
  if (elem) {
    elem.style.display = 'block'
    elem.innerHTML = msg
  }
}

function hideErrorMsg(elem) {
  if (elem) {
    elem.style.display = 'none'
    elem.innerHTML = ''
  }
}

function showLoader(submitBtn) {
  submitBtn.innerText = ''
  submitBtn.disabled = true
  submitBtn.insertAdjacentHTML(
    'beforeend',
    `
    <div class="loader">
      <div class="loader_circle first"></div>
      <div class="loader_circle second"></div>
      <div class="loader_circle"></div>
    </div>
  `,
  )
}

function hideLoader(submitBtn, text) {
  submitBtn.innerText = text
  submitBtn.disabled = false
}

function disableForm(form) {
  const formElements = form.querySelectorAll('input, textarea')
  formElements.forEach(element => {
    element.classList.add('disabled')
    element.disabled = true
  })
}

function enableForm(form) {
  const formElements = form.querySelectorAll('input, textarea')
  formElements.forEach(element => {
    element.classList.remove('disabled')
    element.disabled = false
  })
}

function resetForm(form) {
  form.reset()
  if (window.grecaptcha) {
    window.grecaptcha.reset()
  }
}

function showSuccessModal(elements) {
  elements.successMessage?.classList.add('show')
  elements.overlay?.classList.add('opened')
}

function closeSuccessModal(elements) {
  elements.successMessage?.classList.remove('show')
  elements.overlay?.classList.remove('opened')
}

export { initForm }
